import React, { useEffect, useState, useMemo } from "react";
import {
  Grid,
  Card,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  MenuItem,
  Box,
  Divider,
  InputAdornment,
  Chip,
  CardContent,
  CardActions,
  CardHeader,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";

import LogoutButton from "layouts/logout";
import { useNavigate } from "react-router-dom";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import NotificationsIcon from "@mui/icons-material/Notifications";
import SearchIcon from "@mui/icons-material/Search";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import api from "core/interceptors/api";
import Swal from "sweetalert2";

/* ---------------- PLAN CARD ---------------- */
const PlanCard = ({ plan, onEdit, onDelete }) => (
  <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
    <CardHeader
      title={<MDTypography variant="h6">{plan.name}</MDTypography>}
      action={
        <Chip
          label={plan.plan_type}
          color={plan.plan_type === "vendor" ? "success" : "info"}
          size="small"
          sx={{ textTransform: "capitalize" }}
        />
      }
    />

    <CardContent sx={{ flexGrow: 1 }}>
      <MDTypography variant="h4" fontWeight="bold">
        ₹{Number(plan.total_amount).toLocaleString("en-IN")}
      </MDTypography>

      <MDTypography variant="caption">
        Valid for {plan.validity_days} days
      </MDTypography>

      <Divider sx={{ my: 2 }} />

      <MDTypography variant="body2">Base: ₹{plan.amount}</MDTypography>
      <MDTypography variant="body2">
        GST ({plan.gst_percentage}%): ₹{plan.gst_amount}
      </MDTypography>

      {plan.plan_type === "vendor" && (
        <>
          <Divider sx={{ my: 2 }} />
          <MDBox display="flex" alignItems="center" mb={1}>
            <CheckCircleIcon color="success" fontSize="small" sx={{ mr: 1 }} />
            <MDTypography>{plan.no_of_products} Products</MDTypography>
          </MDBox>

          <MDBox display="flex" alignItems="center">
            <NotificationsIcon color="info" fontSize="small" sx={{ mr: 1 }} />
            <MDTypography>
              {plan.no_of_notifications} Notifications
            </MDTypography>
          </MDBox>
        </>
      )}
    </CardContent>

    <CardActions sx={{ justifyContent: "center" }}>
      <IconButton onClick={() => onEdit(plan)}>
        <EditIcon color="success" />
      </IconButton>
      <IconButton onClick={() => onDelete(plan.id)}>
        <DeleteIcon color="error" />
      </IconButton>
    </CardActions>
  </Card>
);

/* ---------------- MAIN PAGE ---------------- */
function PaymentPlansPage() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);

  const [openForm, setOpenForm] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);

  const [formData, setFormData] = useState({
    gst_percentage: 18,
    plan_type: "",
    period_unit: "day",
  });

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const res = await api.get("/payment_plans/get");
      setPlans(res.data || []);
    } catch {
      Swal.fire("Error", "Failed to load plans", "error");
    } finally {
      setLoading(false);
    }
  };

  const filteredPlans = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return plans.filter(
      (p) =>
        p.name?.toLowerCase().includes(q) ||
        p.plan_type?.toLowerCase().includes(q)
    );
  }, [plans, searchQuery]);

  /* ---------------- SAVE PLAN ---------------- */
  const submitForm = async () => {
    const payload = {
      ...formData,
      plan_type: formData.plan_type,

      // ✅ FINAL GUARANTEE
      no_of_products:
        formData.plan_type === "service"
          ? 0
          : Number(formData.no_of_products || 0),

      no_of_notifications:
        formData.plan_type === "service"
          ? 0
          : Number(formData.no_of_notifications || 0),
    };

    try {
      const url = isUpdate
        ? `/payment_plans/${formData.id}`
        : "/payment_plans/";
      const method = isUpdate ? "put" : "post";

      await api[method](url, payload);
      Swal.fire("Success", "Plan saved successfully", "success");
      setOpenForm(false);
      fetchPlans();
    } catch {
      Swal.fire("Error", "Failed to save plan", "error");
    }
  };

  return (
    <DashboardLayout>
      <MDBox display="flex" justifyContent="flex-end">
        <LogoutButton onLogout={() => navigate("/login")} />
      </MDBox>

      <DashboardNavbar />

      <MDBox pt={4}>
        <Card sx={{ p: 2, mb: 3 }}>
          <MDBox display="flex" justifyContent="space-between">
            <TextField
              size="small"
              label="Search plans"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <MDButton
              color="success"
              startIcon={<AddCircleOutlineIcon />}
              onClick={() => {
                setFormData({
                  gst_percentage: 18,
                  plan_type: "",
                  period_unit: "day",
                  no_of_products: 0,
                  no_of_notifications: 0,
                });
                setIsUpdate(false);
                setOpenForm(true);
              }}
            >
              Create Plan
            </MDButton>
          </MDBox>
        </Card>

        {loading ? (
          <MDBox textAlign="center">
            <CircularProgress />
          </MDBox>
        ) : (
          <Grid container spacing={3}>
            {filteredPlans.map((p) => (
              <Grid item xs={12} md={4} key={p.id}>
                <PlanCard
                  plan={p}
                  onEdit={(d) => {
                    setFormData(d);
                    setIsUpdate(true);
                    setOpenForm(true);
                  }}
                  onDelete={async (id) => {
                    const c = await Swal.fire({
                      title: "Delete plan?",
                      showCancelButton: true,
                    });
                    if (c.isConfirmed) {
                      await api.delete(`/payment_plans/${id}`);
                      fetchPlans();
                    }
                  }}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </MDBox>

      {/* ---------------- FORM MODAL ---------------- */}
      <Dialog open={openForm} onClose={() => setOpenForm(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{isUpdate ? "Update Plan" : "Create Plan"}</DialogTitle>

        <DialogContent>
          <Grid container spacing={2} mt={0.5}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Plan Type</InputLabel>
                <Select
                  value={formData.plan_type}
                  label="Plan Type"
                  onChange={(e) => {
                    const type = e.target.value;
                    setFormData({
                      ...formData,
                      plan_type: type,
                      no_of_products: type === "service" ? 0 : formData.no_of_products,
                      no_of_notifications:
                        type === "service" ? 0 : formData.no_of_notifications,
                    });
                  }}
                >
                  <MenuItem value="vendor">Vendor</MenuItem>
                  <MenuItem value="service">Service Provider</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Plan Name"
                value={formData.name || ""}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Base Amount (₹)"
                value={formData.amount || ""}
                onChange={(e) =>
                  setFormData({ ...formData, amount: e.target.value })
                }
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="GST %"
                value={formData.gst_percentage}
                onChange={(e) =>
                  setFormData({ ...formData, gst_percentage: e.target.value })
                }
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Period Value"
                value={formData.period_value || ""}
                onChange={(e) =>
                  setFormData({ ...formData, period_value: e.target.value })
                }
              />
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Period Unit</InputLabel>
                <Select
                  value={formData.period_unit}
                  label="Period Unit"
                  onChange={(e) =>
                    setFormData({ ...formData, period_unit: e.target.value })
                  }
                >
                  <MenuItem value="day">Days</MenuItem>
                  <MenuItem value="month">Months</MenuItem>
                  <MenuItem value="year">Years</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {formData.plan_type === "vendor" && (
              <>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Products Allowed"
                    value={formData.no_of_products || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        no_of_products: e.target.value,
                      })
                    }
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Notifications"
                    value={formData.no_of_notifications || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        no_of_notifications: e.target.value,
                      })
                    }
                  />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>

        <DialogActions>
          <MDButton onClick={() => setOpenForm(false)}>Cancel</MDButton>
          <MDButton onClick={submitForm} color="info">
            Save
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default PaymentPlansPage;
